import { useCallback, useEffect, useState } from 'react';
import {
  operatorConsole,
  type CallbackAction,
  type CallbackLogsResponse,
} from '../../lib/api/operatorConsole';
import { Button } from '../../components/ui/Button';
import { CallbackLogRow } from './CallbackLogRow';
import { CallbackLogDetailModal } from './CallbackLogDetailModal';

const PER_PAGE = 25;

type ActionOption = CallbackAction | '';
type SuccessOption = '' | 'true' | 'false';

const ACTION_OPTIONS: { value: ActionOption; label: string }[] = [
  { value: '', label: 'All actions' },
  { value: 'get_session', label: 'Get Session' },
  { value: 'get_balance', label: 'Get Balance' },
  { value: 'debit', label: 'Debit' },
  { value: 'credit', label: 'Credit' },
  { value: 'refund', label: 'Refund' },
  { value: 'shipping_debit', label: 'Shipping Debit' },
  { value: 'shipping_settle', label: 'Shipping Settle' },
];

const STATUS_OPTIONS: { value: SuccessOption; label: string }[] = [
  { value: '', label: 'All statuses' },
  { value: 'true', label: 'Success' },
  { value: 'false', label: 'Failed' },
];

const selectClass =
  'rounded-md border border-ui-border bg-ui-card px-3 py-2 text-sm text-ui-text ' +
  'focus:outline-none focus:ring-2 focus:ring-[var(--co-brand)]';

export function CallbackLogsPage() {
  const [data, setData] = useState<CallbackLogsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  // Filters.
  const [actionFilter, setActionFilter] = useState<ActionOption>('');
  const [successFilter, setSuccessFilter] = useState<SuccessOption>('');
  const [externalTxId, setExternalTxId] = useState('');

  // Selected log id drives the detail modal (null = closed).
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await operatorConsole.listCallbackLogs({
        page,
        per: PER_PAGE,
        ...(actionFilter ? { action_filter: actionFilter } : {}),
        ...(successFilter ? { success: successFilter } : {}),
        ...(externalTxId.trim() ? { external_transaction_id: externalTxId.trim() } : {}),
      });
      setData(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [page, actionFilter, successFilter, externalTxId]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Changing any filter resets to page 1 (the effect above then refetches).
  const handleActionChange = (value: ActionOption) => {
    setActionFilter(value);
    setPage(1);
  };
  const handleStatusChange = (value: SuccessOption) => {
    setSuccessFilter(value);
    setPage(1);
  };
  const handleExternalTxChange = (value: string) => {
    setExternalTxId(value);
    setPage(1);
  };

  const totalPages = data ? Math.ceil(data.meta.total_count / data.meta.per) : 0;
  const canPrev = page > 1;
  const canNext = page < totalPages;

  return (
    <div className="space-y-4 p-6">
      <h1 className="text-2xl font-bold">Callback Logs</h1>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-ui-border bg-ui-card p-3">
        <select
          aria-label="Action filter"
          value={actionFilter}
          onChange={(e) => handleActionChange(e.target.value as ActionOption)}
          className={selectClass}
        >
          {ACTION_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>

        <select
          aria-label="Status filter"
          value={successFilter}
          onChange={(e) => handleStatusChange(e.target.value as SuccessOption)}
          className={selectClass}
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>

        <input
          type="text"
          aria-label="External transaction id filter"
          value={externalTxId}
          onChange={(e) => handleExternalTxChange(e.target.value)}
          placeholder="External Tx Id"
          className="w-56 rounded-md border border-ui-border bg-ui-card px-3 py-2 text-sm text-ui-text placeholder:text-ui-text-secondary focus:outline-none focus:ring-2 focus:ring-[var(--co-brand)]"
        />
      </div>

      {loading ? (
        <div className="rounded-lg border border-ui-border bg-ui-card p-6 text-center text-ui-text-secondary">
          Loading callback logs...
        </div>
      ) : error ? (
        <div className="rounded-lg border border-ui-border bg-ui-card p-6 text-center text-[var(--co-error)]">
          Error: {error}
        </div>
      ) : !data || data.callback_logs.length === 0 ? (
        <div className="rounded-lg border border-ui-border bg-ui-card p-6 text-center text-ui-text-secondary">
          No callback logs found
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-ui-border">
                  <th className="px-4 py-3 text-left text-sm font-semibold text-ui-text">Time</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-ui-text">Action</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-ui-text">Method</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-ui-text">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-ui-text">Duration</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-ui-text">External Tx Id</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-ui-text"></th>
                </tr>
              </thead>
              <tbody>
                {data.callback_logs.map((log) => (
                  <CallbackLogRow key={log.id} log={log} onClick={setSelectedId} />
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between rounded-lg border border-ui-border bg-ui-card px-4 py-3">
            <div className="text-sm text-ui-text-secondary">
              Page {page} of {totalPages} • Total {data.meta.total_count} logs
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={!canPrev}
                className="px-3 py-1 text-sm disabled:opacity-50"
              >
                Prev
              </Button>
              <Button
                onClick={() => setPage((p) => p + 1)}
                disabled={!canNext}
                className="px-3 py-1 text-sm disabled:opacity-50"
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}

      <CallbackLogDetailModal
        logId={selectedId}
        onClose={() => setSelectedId(null)}
        onResent={() => {
          // A replay created a new log — refetch so it appears in the list.
          void fetchLogs();
        }}
      />
    </div>
  );
}
